import React, { useEffect, useState } from 'react'
import InlineSVG from 'react-inlinesvg'
import { Modal, ModalBody, ModalHeader, Row } from 'reactstrap'
import xIcon from '../../assets/svg/x.svg';
import useFetchData from '../../hooks/fetch-data.hook';
import { Player } from '@lottiefiles/react-lottie-player';
import useFormV2 from '../../form-v2/form.hook';
import FormV2 from '../../form-v2/form.component';
import Field from '../../form-v2/field.component';

const defaults = {
    wsdlUrl: process.env.NODE_ENV === 'development' ? 'https://lpservices.validering.infosolutions.se/LPServices.svc?singleWsdl' : 'https://lpservices.infosolutions.se/lpservices.svc?singlewsdl',
    dashboardUrl: process.env.NODE_ENV === 'development' ? 'https://labportalen.validering.infosolutions.se/loggain.aspx' : 'https://login.labportalen.se/loggain.aspx',
}

const CreateOrUpdateLabportalenOrganisation = ({
    clinicId,
    wsdlUrl,
    dashboardUrl,
    systemId,
    userId,
    active,
    closeModal,
    data,
    lang,
    onAfterSubmit,
}) => {
    const { data: fetchData, isLoading } = useFetchData(`/integrations/admin/labportalen/organisation/${clinicId}`)

    return (
        <Modal
            centered
            size='xl'
            isOpen={true}
            toggle={closeModal}
        >
            <ModalHeader
                close={
                    <button
                        type="button"
                        className="close"
                        style={{ position: "absolute", top: "15px", right: "15px" }}
                        onClick={closeModal}
                    >
                        <InlineSVG src={xIcon} />
                    </button>
                }
            >
                {data ? 'Update Organisation'.translate(lang) : 'Create Organisation'.translate(lang)}
            </ModalHeader>
            <ModalBody
                className='form-modal-loading'
            >
                {isLoading ? (
                    <div className="loader-wrap" >
                        <Player
                            autoplay={true}
                            loop={true}
                            src="/lf30_editor_l5cxzdyf.json"
                            style={{ height: typeof window != 'undefined' && window.innerWidth < 768 ? "128px" : "256px", width: typeof window != 'undefined' && window.innerWidth < 768 ? "128px" : "256px" }}
                        ></Player>
                    </div>
                ) : (
                    <Form
                        clinicId={clinicId}
                        wsdlUrl={wsdlUrl}
                        dashboardUrl={dashboardUrl}
                        systemId={systemId}
                        userId={userId}
                        active={active}
                        closeModal={closeModal}
                        data={data}
                        lang={lang}
                        clinicUsers={fetchData?.users}
                        clinicAdmin={fetchData?.admin}
                        clinicDepartments={fetchData?.departments}
                        clinicData={fetchData?.data}
                        onAfterSubmit={onAfterSubmit}
                    />
                )}
            </ModalBody>
        </Modal>
    )
}

const Form = ({
    clinicId,
    wsdlUrl,
    dashboardUrl,
    systemId,
    userId,
    active,
    closeModal,
    data,
    lang,

    clinicUsers,
    clinicAdmin,
    clinicDepartments,
    clinicData,
    onAfterSubmit
}) => {

    const formData = useFormV2({
        autoComplete: false,
        autoSubmit: {
            url: `/integrations/admin/labportalen/organisation/${clinicId}`,
            method: 'post',
            onBeforeSubmit: (data, _, e) => {
                data._type = e?.type;
                if (data.admin?.addAdmin === true) {
                    // if (data.admin.CanMakeRequisitionsLevel === true) data.admin.CanMakeRequisitionsLevel = 1;
                    // else data.admin.CanMakeRequisitionsLevel = 0;

                    if (data.admin.CanMakeResultsProtected === true) data.admin.CanMakeResultsProtected = 1;
                    else data.admin.CanMakeResultsProtected = 0;

                    if (data.admin.CanSignResults === true) data.admin.CanSignResults = 1;
                    else data.admin.CanSignResults = 0;

                    if (typeof data.admin.Userrole === 'string') data.admin.Userrole = Number(data.admin.Userrole);
                    if (typeof data.admin.CanMakeRequisitionsLevel === 'string') data.admin.CanMakeRequisitionsLevel = Number(data.admin.CanMakeRequisitionsLevel);

                    if ([1, 3].includes(data.admin.CanMakeRequisitionsLevel)) data.admin.CanMakeRequisitions = true;
                    else data.admin.CanMakeRequisitions = false;

                    if ([2, 3].includes(data.admin.CanMakeRequisitionsLevel)) data.admin.MakesRequisitionsForOthers = true;
                    else data.admin.MakesRequisitionsForOthers = false;
                }
                return data;
            },
            onAfterSubmit,
        },
        buttons: {
            reset: {
                isShown: true,
                text: 'Close',
                callback: () => closeModal?.(),
                color: 'danger',
            },
        },
        tabs: {
            'intg': {
                label: 'Integration',
            },
            'org': {
                label: 'Organisation',
            },
            'dept': {
                label: 'Departments',
            },
            'admin': {
                label: 'Admin',
                condition: (_, e) => !e?.userId,
            }
        },
        sections: {
            'intg': {
                tab: 'intg',
                title: 'Integration',
                subtitle: 'Basic integration settings',
            },
            'org': {
                tab: 'org',
                title: 'Organisation',
                subtitle: 'Organisation data',
            },
            'dept': {
                tab: 'dept',
                title: 'Departments',
                subtitle: 'Departments data (optional) - if connected, departments will be used in iframes to show data for each department.',
            },
            'admin': {
                tab: 'admin',
                title: 'Admin',
                subtitle: 'Create main admin user for the organisation. Admin USERID is needed for proper integration management (deleting organisations).',
                condition: (_, e) => !e?.userId,
            },
            'admin-perm': {
                tab: 'admin',
                title: 'Roles and Permissions',
                subtitle: 'Admin role and permissions',
                condition: (state, e) => !e?.userId && state?.['admin.addAdmin']?.value === true,
            }
        }
    }, {
        // Int
        active: {
            type: 'switch',
            label: 'Active',
            initialValue: active ?? false,
            section: 'intg',
        },
        wsdlUrl: {
            type: 'url',
            label: 'WSDL URL (single file)',
            isRequired: true,
            initialValue: wsdlUrl || defaults.wsdlUrl,
            section: 'intg',
        },
        dashboardUrl: {
            type: 'url',
            label: 'Dashboard URL',
            isRequired: true,
            initialValue: dashboardUrl || defaults.dashboardUrl,
            section: 'intg',
        },
        systemId: {
            type: 'text',
            label: 'System ID',
            isRequired: true,
            initialValue: systemId,
            section: 'intg',
        },

        // Org
        'data.name': {
            type: 'text',
            label: 'Name',
            isRequired: true,
            section: 'org',
            cols: { lg: 6 }
        },
        'data.organisationNumber': {
            type: 'text',
            label: 'Organisation number',
            isRequired: true,
            config: {
                min: 10,
                max: 10,
            },
            section: 'org',
            cols: { lg: 6 },
            instantTransformer: (value) => value?.replace(/\D/g, ''),
            submitTransformer: (value) => value?.replace(/\D/g, ''),
        },
        'data.phone': {
            type: 'text',
            label: 'Phone Number',
            isRequired: true,
            section: 'org',
            cols: { lg: 6 },
            instantTransformer: (value) => value?.replace(/\D/g, '').slice(0, 15),
            submitTransformer: (value) => value?.replace(/\D/g, '').slice(0, 15),
            validator: (value) => value === '' || (value?.length <= 15 && /^\d+$/.test(value)) ? null : 'Please enter a valid phone number!',
        },
        'data.fax': {
            type: 'text',
            label: 'Fax Number',
            section: 'org',
            cols: { lg: 6 },
            instantTransformer: (value) => value?.replace(/\D/g, '').slice(0, 15),
            submitTransformer: (value) => value?.replace(/\D/g, '').slice(0, 15),
            validator: (value) => value === '' || (value?.length <= 15 && /^\d+$/.test(value)) ? null : 'Please enter a valid phone number!',
        },
        'data.address': {
            type: 'text',
            label: 'Address',
            section: 'org',
            isRequired: true,
            cols: { lg: 5 }
        },
        'data.city': {
            type: 'text',
            label: 'City',
            section: 'org',
            isRequired: true,
            cols: { lg: 5 }
        },
        'data.zipcode': {
            type: 'text',
            label: 'Zipcode',
            section: 'org',
            isRequired: true,
            cols: { lg: 2 }
        },
        'data.hsaid': {
            type: 'text',
            label: 'HSA ID',
            section: 'org',
            cols: { lg: 6 }
        },
        'data.allowChangeOfOrderUnit': {
            type: 'switch',
            label: 'Allow change of order unit',
            section: 'org',
            // cols: { lg: 6 }
        },

        // Depts
        clinics: {
            type: 'custom',
            initialValue: [],
            component: ClinicsField,
            section: 'dept',
            condition: (_, e) => e?.clinicDepartments && e.clinicDepartments?.length > 0,
            validator: (value) => {
                let isValid = true;

                if (!Array.isArray(value)) return 'Please enter all required fields, for each department!';

                for (const obj of value) {
                    if (obj && obj.clinic && obj.active) {
                        if (
                            !obj.data ||
                            !obj.data.name?.trim?.() ||
                            !obj.data.phone?.trim?.() ||
                            !obj.data.street?.trim?.() ||
                            !obj.data.city?.trim?.() ||
                            !obj.data.zip?.trim?.()
                        ) {
                            isValid = false;
                            break;
                        }
                    }
                }

                return isValid ? null : 'Please enter all required fields, for each department!'
            }
        },

        // Admin
        'admin.addAdmin': {
            type: 'switch',
            label: 'Add Admin Account',
            section: 'admin',
            condition: (_, e) => !e?.userId,
            initialValue: false,
        },
        'admin._id': {
            type: 'hidden',
            condition: (state) => state?.['admin.addAdmin']?.value === true,
        },
        'admin.Usertype': {
            type: 'hidden',
            initialValue: 0,
            condition: (state) => state?.['admin.addAdmin']?.value === true,
        },
        'admin.EditOrderCodes': {
            type: 'hidden',
            initialValue: null,
            condition: (state) => state?.['admin.addAdmin']?.value === true,
        },
        'admin.Departmentid': {
            type: 'hidden',
            initialValue: 0,
            condition: (state) => state?.['admin.addAdmin']?.value === true,
        },
        'admin.Name': {
            type: 'text',
            label: 'Name',
            isRequired: true,
            section: 'admin',
            cols: { lg: 6 },
            condition: (state) => state?.['admin.addAdmin']?.value === true,
            info: 'Please enter the full name of the user.'
        },
        'admin.Usercode': {
            type: 'text',
            label: 'Username',
            isRequired: true,
            section: 'admin',
            cols: { lg: 6 },
            instantTransformer: (value) => value?.toLowerCase().replace(/\s/g, ''),
            submitTransformer: (value) => value?.toLowerCase().replace(/\s/g, ''),
            info: 'LabPortalen might prefix the username with organisation id!',
            condition: (state) => state?.['admin.addAdmin']?.value === true,
        },
        'admin.Email': {
            type: 'email',
            label: 'Email',
            isRequired: true,
            section: 'admin',
            cols: { lg: 6 },
            condition: (state) => state?.['admin.addAdmin']?.value === true,
            info: 'It is mandatory to enter e-mail.'
        },
        'admin.Password': {
            type: 'hidden',
            label: 'Password',
            initialValue: '',
            // isRequired: true,
            section: 'admin',
            cols: { lg: 6 },
            condition: (state) => state?.['admin.addAdmin']?.value === true,
        },
        'admin.Phone': {
            type: 'text',
            label: 'Phone',
            isRequired: true,
            section: 'admin',
            cols: { lg: 6 },
            condition: (state) => state?.['admin.addAdmin']?.value === true,
            instantTransformer: (value) => value?.replace(/\D/g, '').slice(0, 15),
            submitTransformer: (value) => value?.replace(/\D/g, '').slice(0, 15),
            validator: (value) => value === '' || (value?.length <= 15 && /^\d+$/.test(value)) ? null : 'Please enter a valid phone number!',
            info: 'The number is entered with numbers only and without spaces. For example, 46712345678 or 0712345678.'
        },
        'admin.Cellphone': {
            type: 'text',
            label: 'Cellphone',
            section: 'admin',
            cols: { lg: 6 },
            condition: (state) => state?.['admin.addAdmin']?.value === true,
            instantTransformer: (value) => value?.replace(/\D/g, '').slice(0, 15),
            submitTransformer: (value) => value?.replace(/\D/g, '').slice(0, 15),
            validator: (value) => value === '' || (value?.length <= 15 && /^\d+$/.test(value)) ? null : 'Please enter a valid phone number!',
            info: 'The number is entered with numbers only and without spaces. For example, 46712345678 or 0712345678.'
        },
        'admin.Hsaid': {
            type: 'text',
            label: 'HSA ID',
            section: 'admin',
            cols: { lg: 6 },
            condition: (state) => state?.['admin.addAdmin']?.value === true,
            info: 'HSA-ID must be specified if the user is to log in with SITHS card. Enter complete HAS-ID (all characters). For example: SE165567766992-10PB.'
        },

        'admin.Userrole': {
            type: 'select',
            initialValue: '1',
            isRequired: true,
            condition: (state) => state?.['admin.addAdmin']?.value === true,
            label: 'Role',
            config: {
                options: [{
                    value: '0',
                    label: 'Inaktiv'
                }, {
                    value: '1',
                    label: 'Användare'
                }, {
                    value: '2',
                    label: 'Administratör'
                },
                // {
                //     value: '3',
                //     label: 'Avtalsansvarig'
                // }, 
                {
                    value: '100',
                    label: 'Provtagare'
                }]
            },
            section: 'admin-perm',
            cols: { lg: 6 },
            description: (s) => {
                const role = s?.['admin.Userrole']?.value;
                if (role === '0') return 'Has no longer access to LabPortalen but can be activated by Administrator.';
                if (role === '1') return 'Has access to Incoming/Outgoing/Patient overview.';
                if (role === '2') return 'Has access to Incoming/Outgoing/Patient overview and Medical administration and administration of users below Account administration.';
                if (role === '100') return 'Has access to Patient overview.';

                return 'Please select a role.';
            }
        },
        'admin.CanMakeRequisitionsLevel': {
            type: 'select',
            label: 'Competence Level',
            section: 'admin-perm',
            cols: { lg: 6 },
            placeholder: 'Please select',
            condition: (state) => state?.['admin.addAdmin']?.value === true,
            config: {
                options: [
                    {
                        value: '0',
                        label: 'May not create eRemiss'
                    },
                    {
                        value: '1',
                        label: 'May create eRemiss in their own name'
                    },
                    {
                        value: '2',
                        label: 'May only create eRemiss for others'
                    },
                    {
                        value: '3',
                        label: 'May create eRemiss in their own name and for others'
                    }
                ]
            },
            description: (s) => {
                const level = s?.['admin.CanMakeRequisitionsLevel']?.value;
                if (level === '0') return 'Does not have the right to create e-referrals in LabPortalen.';
                if (level === '1') return 'Has the right to create e-referrals in LabPortalen for themselves.';
                if (level === '2') return 'Has the right to create e-referrals in LabPortalen only for other users.';
                if (level === '3') return 'Has the right to create e-referrals both for themselves and other users.';

                return 'Please select a competence level.';
            }
        },
        // 'admin.CanMakeRequisitions': {
        //     type: 'switch',
        //     label: 'Can make requisitions',
        //     section: 'admin-perm',
        //     cols: { lg: 6, xl: 4 },
        //     condition: (state) => state?.['admin.addAdmin']?.value === true,
        // },
        'admin.CanMakeResultsProtected': {
            type: 'checkbox',
            label: 'Can make results protected',
            section: 'admin-perm',
            initialValue: true,
            // cols: { lg: 6, xl: 4 },
            condition: (state) => state?.['admin.addAdmin']?.value === true,
            info: "This setting should be set to the users who should have the ability to block journal entries in LabPortalen at the patient's request."
        },
        'admin.CanSignResults': {
            type: 'checkbox',
            label: 'Can sign results',
            section: 'admin-perm',
            initialValue: true,
            // cols: { lg: 6, xl: 4 },
            // onChangeCallback: (value) => {
            //     if (value === false) {
            //         formData?.set?.('admin.CanSignDepartmentresults', false);
            //         formData?.set?.('admin.CanSignOrganisationresults', false);
            //     }
            // },
            condition: (state) => state?.['admin.addAdmin']?.value === true,
            info: "This setting should be set on the users who have signing responsibilities in LabPortalen. A user who should register local results needs to have this setting activated."
        },
        // 'admin.CanSignDepartmentresults': {
        //     type: 'checkbox',
        //     label: 'Can sign department results',
        //     section: 'admin-perm',
        //     // cols: { lg: 6, xl: 4 },
        //     condition: (state) => state?.['admin.addAdmin']?.value === true,
        //     isDisabled: (s) => s?.['admin.CanSignResults']?.value !== true,
        // },
        // 'admin.CanSignOrganisationresults': {
        //     type: 'checkbox',
        //     label: 'Can sign organisation results',
        //     section: 'admin-perm',
        //     // cols: { lg: 6, xl: 4 },
        //     condition: (state) => state?.['admin.addAdmin']?.value === true,
        //     isDisabled: (s) => s?.['admin.CanSignResults']?.value !== true,
        // },
        // 'admin.MakesRequisitionsForOthers': {
        //     type: 'switch',
        //     label: 'Makes requisitions for others',
        //     section: 'admin-perm',
        //     cols: { lg: 6, xl: 4 },
        //     condition: (state) => state?.['admin.addAdmin']?.value === true,
        // },
    }, {
        clinicId,
        clinicUsers,
        clinicAdmin,
        clinicDepartments,
        clinicData,
        type: data ? 'update' : 'create',
        userId,
    });

    // console.log('formData', formData)

    useEffect(() => {
        let _clinicData = clinicData ? { ...clinicData } : {};
        if (_clinicData?.phone && typeof _clinicData?.phone === 'string') _clinicData.phone = _clinicData.phone.replace(/\D/g, '').slice(0, 15);
        if (_clinicData?.fax && typeof _clinicData?.fax === 'string') _clinicData.fax = _clinicData.fax.replace(/\D/g, '').slice(0, 15);

        let _adminData = clinicAdmin ? { ...clinicAdmin } : {};
        if (_adminData?.Phone && typeof _adminData?.Phone === 'string') _adminData.Phone = _adminData.Phone.replace(/\D/g, '').slice(0, 15);
        if (_adminData?.Cellphone && typeof _adminData?.Cellphone === 'string') _adminData.Cellphone = _adminData.Cellphone.replace(/\D/g, '').slice(0, 15);

        let _clinicDepartments = clinicDepartments ? [...clinicDepartments] : [];
        if (_clinicDepartments?.length > 0) {
            for (let i = 0; i < _clinicDepartments.length; i++) {
                let obj = _clinicDepartments[i];
                if (obj?.data?.phone && typeof obj?.data?.phone === 'string') _clinicDepartments[i].data.phone = obj.data.phone.replace(/\D/g, '').slice(0, 15);
            }
        }

        formData.handlePopulate?.({
            data: _clinicData || {},
            admin: _adminData || {},
            clinics: _clinicDepartments || [],
        })
    }, [])

    return <FormV2
        data={formData}
        lang={lang}
    />
}

export default CreateOrUpdateLabportalenOrganisation;

/**
 * 
 * @param {import('../../form-v2/types').FormV2FieldProps} props 
 */
const ClinicsField = (props) => {
    return (
        <div>
            {props.value?.map?.((obj, i) => {
                const onChange = (val) => {
                    const newValue = [...props.value].map(item => {
                        if (item.clinic === obj.clinic) {
                            return {
                                ...item,
                                ...val,
                            }
                        }

                        return item;
                    });
                    props.onChange?.(newValue);
                }
                const onBlur = () => {
                    props.onBlur?.();
                }
                return <ClinicField
                    key={obj.clinic ?? i}
                    props={props}
                    value={obj}
                    index={i}
                    onChange={onChange}
                    onBlur={onBlur}
                />
            })}
        </div>
    )
}

/**
 * 
 * @param {{
 *   props: import('../../form-v2/types').FormV2FieldProps;
 *   value: {
 *     active: boolean;
 *     name: string;
 *     clinic: string;
 *     labPortalenId?: string | number;
 *     data: {
 *       name: string;
 *       phone: string;
 *       publicInfo: string;
 *       street: string;
 *       city: string;
 *       zip: string;
 *       drsReceiver: boolean;
 *       drsRequester: boolean;
 *       hsaid: string;
 *     };
 *   };
 *   index: number;
 *   onChange: (val: any) => void;
 *   onBlur: () => void;
 * }} param0 
 */
const ClinicField = ({
    props,
    value,
    index,
    onChange,
    onBlur,
}) => {
    const isSubmitAttempted = props?.data?.submitAttempted ?? false;

    const [isTouched, setIsTouched] = useState({
        active: false,
        name: false,
        phone: false,
        publicInfo: false,
        street: false,
        city: false,
        zip: false,
        drsReceiver: false,
        drsRequester: false,
        hsaid: false,
    });
    const [errors, setErrors] = useState({
        active: null,
        name: !value?.active ? null : value?.data?.name?.trim?.() ? null : 'This field is required!',
        phone: !value?.active ? null : value?.data?.phone?.trim?.() ? null : 'This field is required!',
        publicInfo: null,
        street: !value?.active ? null : value?.data?.street?.trim?.() ? null : 'This field is required!',
        city: !value?.active ? null : value?.data?.city?.trim?.() ? null : 'This field is required!',
        zip: !value?.active ? null : value?.data?.zip?.trim?.() ? null : 'This field is required!',
        drsReceiver: null,
        drsRequester: null,
        hsaid: null,
    });

    useEffect(() => {
        if (isSubmitAttempted) setIsTouched(prev => ({
            ...prev,
            active: true,
            name: true,
            phone: true,
            publicInfo: true,
            street: true,
            city: true,
            zip: true,
            drsReceiver: true,
            drsRequester: true,
            hsaid: true,
        }))
    }, [isSubmitAttempted]);

    if (!value || !props) return null;
    return <Row style={{ marginTop: 10, marginBottom: 10 }}>
        <h6>{value?.name}</h6>
        <Field
            key='active'
            data={props.data}
            fieldName={props.fieldName}
            lang={props.lang}
            refresh={props.refresh}
            isSubmitting={props.isSubmitting}
            _singleField={{
                type: 'switch',
                label: 'Active',
                id: `${value.clinic || index}.active`,
                fieldState: {
                    value: value.active ?? false,
                    hasError: errors.active !== null,
                    isTouched: isTouched.active,
                    isValid: errors.active === null,
                    errorMessage: errors.active,
                },
                handleChange: (o) => {
                    setIsTouched(prev => ({ ...prev, active: true }));

                    const newValue = {
                        ...value,
                        active: o.value,
                    };
                    onChange?.(newValue);

                    setErrors(prev => ({
                        ...prev,
                        name: !o.value ? null : newValue?.data?.name?.trim?.() ? null : 'This field is required!',
                        phone: !o.value ? null : newValue?.data?.phone?.trim?.() ? null : 'This field is required!',
                        street: !o.value ? null : newValue?.data?.street?.trim?.() ? null : 'This field is required!',
                        city: !o.value ? null : newValue?.data?.city?.trim?.() ? null : 'This field is required!',
                        zip: !o.value ? null : newValue?.data?.zip?.trim?.() ? null : 'This field is required!',
                    }))
                },
                handleBlur: () => {
                    setIsTouched(prev => ({ ...prev, active: true }));
                    onBlur?.();
                },
            }}
        />
        {value.active === true ? <>
            <Field
                key='name'
                data={props.data}
                fieldName={props.fieldName}
                lang={props.lang}
                refresh={props.refresh}
                isSubmitting={props.isSubmitting}
                _singleField={{
                    type: 'text',
                    label: 'Name',
                    id: `${value.clinic || index}.name`,
                    fieldState: {
                        value: value.data?.name ?? '',
                        hasError: errors.name !== null,
                        isTouched: isTouched.name,
                        isValid: errors.name === null,
                        errorMessage: errors.name,
                    },
                    cols: { lg: 6 },
                    handleChange: (o) => {
                        const newValue = {
                            ...value,
                            data: {
                                ...(value.data || {}),
                                name: o.value,
                            },
                        };
                        onChange?.(newValue);

                        setErrors(prev => ({
                            ...prev,
                            name: !value?.active ? null : newValue?.data?.name?.trim?.() ? null : 'This field is required!',
                        }))
                    },
                    handleBlur: () => {
                        setIsTouched(prev => ({ ...prev, name: true }));
                        onBlur?.();
                    },
                    isRequired: value.active === true,
                }}
            />
            <Field
                key='phone'
                data={props.data}
                fieldName={props.fieldName}
                lang={props.lang}
                refresh={props.refresh}
                isSubmitting={props.isSubmitting}
                _singleField={{
                    type: 'text',
                    label: 'Phone',
                    id: `${value.clinic || index}.phone`,
                    fieldState: {
                        value: value.data?.phone ?? '',
                        hasError: errors.phone !== null,
                        isTouched: isTouched.phone,
                        isValid: errors.phone === null,
                        errorMessage: errors.phone,
                    },
                    cols: { lg: 6 },
                    handleChange: (o) => {
                        const val = o.value?.replace(/\D/g, '').slice(0, 15);
                        const newValue = {
                            ...value,
                            data: {
                                ...(value.data || {}),
                                phone: val,
                            },
                        };
                        onChange?.(newValue);

                        setErrors(prev => ({
                            ...prev,
                            phone: !value?.active ? null : newValue?.data?.phone?.trim?.() ? null : 'This field is required!',
                        }))
                    },
                    handleBlur: () => {
                        setIsTouched(prev => ({ ...prev, phone: true }));
                        onBlur?.();
                    },
                    isRequired: value.active === true,
                }}
            />
            <Field
                key='street'
                data={props.data}
                fieldName={props.fieldName}
                lang={props.lang}
                refresh={props.refresh}
                isSubmitting={props.isSubmitting}
                _singleField={{
                    type: 'text',
                    label: 'Street',
                    id: `${value.clinic || index}.street`,
                    fieldState: {
                        value: value.data?.street ?? '',
                        hasError: errors.street !== null,
                        isTouched: isTouched.street,
                        isValid: errors.street === null,
                        errorMessage: errors.street,
                    },
                    cols: { lg: 5 },
                    handleChange: (o) => {
                        const newValue = {
                            ...value,
                            data: {
                                ...(value.data || {}),
                                street: o.value,
                            },
                        };
                        onChange?.(newValue);

                        setErrors(prev => ({
                            ...prev,
                            street: !value?.active ? null : newValue?.data?.street?.trim?.() ? null : 'This field is required!',
                        }))
                    },
                    handleBlur: () => {
                        setIsTouched(prev => ({ ...prev, street: true }));
                        onBlur?.();
                    },
                    isRequired: value.active === true,
                }}
            />
            <Field
                key='city'
                data={props.data}
                fieldName={props.fieldName}
                lang={props.lang}
                refresh={props.refresh}
                isSubmitting={props.isSubmitting}
                _singleField={{
                    type: 'text',
                    label: 'City',
                    id: `${value.clinic || index}.city`,
                    fieldState: {
                        value: value.data?.city ?? '',
                        hasError: errors.city !== null,
                        isTouched: isTouched.city,
                        isValid: errors.city === null,
                        errorMessage: errors.city,
                    },
                    cols: { lg: 5, md: 8, sm: 8 },
                    handleChange: (o) => {
                        const newValue = {
                            ...value,
                            data: {
                                ...(value.data || {}),
                                city: o.value,
                            },
                        };
                        onChange?.(newValue);

                        setErrors(prev => ({
                            ...prev,
                            city: !value?.active ? null : newValue?.data?.city?.trim?.() ? null : 'This field is required!',
                        }))
                    },
                    handleBlur: () => {
                        setIsTouched(prev => ({ ...prev, city: true }));
                        onBlur?.();
                    },
                    isRequired: value.active === true,
                }}
            />
            <Field
                key='zip'
                data={props.data}
                fieldName={props.fieldName}
                lang={props.lang}
                refresh={props.refresh}
                isSubmitting={props.isSubmitting}
                _singleField={{
                    type: 'text',
                    label: 'Zip',
                    id: `${value.clinic || index}.zip`,
                    fieldState: {
                        value: value.data?.zip ?? '',
                        hasError: errors.zip !== null,
                        isTouched: isTouched.zip,
                        isValid: errors.zip === null,
                        errorMessage: errors.zip,
                    },
                    cols: { lg: 2, md: 4, sm: 4, },
                    handleChange: (o) => {
                        const newValue = {
                            ...value,
                            data: {
                                ...(value.data || {}),
                                zip: o.value,
                            },
                        };
                        onChange?.(newValue);

                        setErrors(prev => ({
                            ...prev,
                            zip: !value?.active ? null : newValue?.data?.zip?.trim?.() ? null : 'This field is required!',
                        }))
                    },
                    handleBlur: () => {
                        setIsTouched(prev => ({ ...prev, zip: true }));
                        onBlur?.();
                    },
                    isRequired: value.active === true,
                }}
            />
            <Field
                key='publicInfo'
                data={props.data}
                fieldName={props.fieldName}
                lang={props.lang}
                refresh={props.refresh}
                isSubmitting={props.isSubmitting}
                _singleField={{
                    type: 'text',
                    label: 'Public info',
                    id: `${value.clinic || index}.publicInfo`,
                    fieldState: {
                        value: value.data?.publicInfo ?? '',
                        hasError: errors.publicInfo !== null,
                        isTouched: isTouched.publicInfo,
                        isValid: errors.publicInfo === null,
                        errorMessage: errors.publicInfo,
                    },
                    cols: { lg: 6 },
                    handleChange: (o) => {
                        const newValue = {
                            ...value,
                            data: {
                                ...(value.data || {}),
                                publicInfo: o.value,
                            },
                        };
                        onChange?.(newValue);
                    },
                    handleBlur: () => {
                        setIsTouched(prev => ({ ...prev, publicInfo: true }));
                        onBlur?.();
                    },
                    // isRequired: value.active === true,
                }}
            />
            <Field
                key='hsaid'
                data={props.data}
                fieldName={props.fieldName}
                lang={props.lang}
                refresh={props.refresh}
                isSubmitting={props.isSubmitting}
                _singleField={{
                    type: 'text',
                    label: 'HSAID',
                    id: `${value.clinic || index}.hsaid`,
                    fieldState: {
                        value: value.data?.hsaid ?? '',
                        hasError: errors.hsaid !== null,
                        isTouched: isTouched.hsaid,
                        isValid: errors.hsaid === null,
                        errorMessage: errors.hsaid,
                    },
                    cols: { lg: 6 },
                    handleChange: (o) => {
                        const newValue = {
                            ...value,
                            data: {
                                ...(value.data || {}),
                                hsaid: o.value,
                            },
                        };
                        onChange?.(newValue);
                    },
                    handleBlur: () => {
                        setIsTouched(prev => ({ ...prev, hsaid: true }));
                        onBlur?.();
                    },
                    // isRequired: value.active === true,
                }}
            />
            <Field
                key='drsReceiver'
                data={props.data}
                fieldName={props.fieldName}
                lang={props.lang}
                refresh={props.refresh}
                isSubmitting={props.isSubmitting}
                _singleField={{
                    type: 'switch',
                    label: 'DRS Receiver',
                    id: `${value.clinic || index}.drsReceiver`,
                    fieldState: {
                        value: value.data.drsReceiver ?? false,
                        hasError: errors.drsReceiver !== null,
                        isTouched: isTouched.drsReceiver,
                        isValid: errors.drsReceiver === null,
                        errorMessage: errors.drsReceiver,
                    },
                    cols: { lg: 6 },
                    handleChange: (o) => {
                        const newValue = {
                            ...value,
                            data: {
                                ...(value.data || {}),
                                drsReceiver: o.value ?? false,
                            },
                        };
                        onChange?.(newValue);
                    },
                    handleBlur: () => {
                        setIsTouched(prev => ({ ...prev, drsReceiver: true }));
                        onBlur?.();
                    },
                    // isRequired: value.active === true,
                }}
            />
            <Field
                key='drsRequester'
                data={props.data}
                fieldName={props.fieldName}
                lang={props.lang}
                refresh={props.refresh}
                isSubmitting={props.isSubmitting}
                _singleField={{
                    type: 'switch',
                    label: 'DRS Requester',
                    id: `${value.clinic || index}.drsRequester`,
                    fieldState: {
                        value: value.data?.drsRequester ?? false,
                        hasError: errors.drsRequester !== null,
                        isTouched: isTouched.drsRequester,
                        isValid: errors.drsRequester === null,
                        errorMessage: errors.drsRequester,
                    },
                    cols: { lg: 6 },
                    handleChange: (o) => {
                        const newValue = {
                            ...value,
                            data: {
                                ...(value.data || {}),
                                drsRequester: o.value ?? false,
                            },
                        };
                        onChange?.(newValue);
                    },
                    handleBlur: () => {
                        setIsTouched(prev => ({ ...prev, drsRequester: true }));
                        onBlur?.();
                    },
                    // isRequired: value.active === true,
                }}
            />
        </> : null}
    </Row>
}